import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Moon, Sun, Home, Users, Settings, Database, Shield, Wrench, Kanban, Mail, LogOut, Search, CalendarClock } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';

interface AdminLayoutProps {
  children: React.ReactNode;
  currentView: string;
  onNavigate: (view: string) => void;
}

export const AdminLayout: React.FC<AdminLayoutProps> = ({ 
  children, 
  currentView, 
  onNavigate
}) => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    try {
      await logout();
      // Redirect to admin login page after successful logout
      navigate('/login/admin', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
      // Even if logout fails, redirect to admin login for security
      navigate('/login/admin', { replace: true });
    }
  };

  const navigationItems = [
    { icon: Home, label: 'Dashboard', view: 'dashboard' },
    { icon: Users, label: 'Client Management', view: 'users' },
    { icon: CalendarClock, label: 'Services', view: 'services' },
    { icon: Database, label: 'Data Management', view: 'data' },
    { icon: Database, label: 'Backup Management', view: 'backup-management' },
    { icon: Mail, label: 'Email Settings', view: 'email' },
    { icon: Settings, label: 'System Settings', view: 'settings' },
    { icon: Shield, label: 'Security', view: 'security' },
    { icon: Search, label: 'Audit Investigation', view: 'audit-investigation' },
    { icon: Wrench, label: 'Integrations', view: 'integrations' },
  ];

  const developmentTools = [
    { icon: Kanban, label: 'Kanban Board', view: 'kanban' },
  ];

  return (
    <div className="app-layout">
      {/* Sidebar */}
      <aside className="sidebar">
        <div className="sidebar-header">
          <h1 className="app-title">Mission Control</h1>
          <span className="app-subtitle">Admin Panel</span>
        </div>

        <nav className="sidebar-nav">
          {/* Main Admin Navigation */}
          {navigationItems.map((item) => {
            const IconComponent = item.icon;
            return (
              <button
                key={item.view}
                onClick={() => onNavigate(item.view)}
                className={`nav-item ${currentView === item.view ? 'active' : ''}`}
              >
                <IconComponent size={20} />
                <span className="nav-label">{item.label}</span>
              </button>
            );
          })}

          {/* Development Tools Section */}
          <div className="nav-section">
            <div className="nav-section-title">Development Tools</div>
            {developmentTools.map((item) => {
              const IconComponent = item.icon;
              return (
                <button
                  key={item.view}
                  onClick={() => onNavigate(item.view)}
                  className={`nav-item ${currentView === item.view ? 'active' : ''}`}
                >
                  <IconComponent size={20} />
                  <span className="nav-label">{item.label}</span>
                </button>
              );
            })}
          </div>
        </nav>

        <div className="sidebar-footer">
          <button className="theme-toggle" onClick={toggleTheme}>
            {theme === 'light' ? <Moon size={20} /> : <Sun size={20} />}
            <span>{theme === 'light' ? 'Dark' : 'Light'} Mode</span>
          </button>

          <div className="user-info">
            <div className="user-details">
              <span className="user-name">{user?.first_name ? `${user.first_name} ${user.last_name || ''}`.trim() : 'Admin User'}</span>
              <span className="user-role">ADMIN</span>
            </div>
            <button className="logout-btn" onClick={handleLogout}>
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content">
        {children}
      </main>

      <style>{`
        .app-layout {
          display: flex;
          height: 100vh;
          background: var(--bg-secondary);
        }

        .sidebar {
          width: 280px;
          background: var(--bg-primary);
          border-right: 1px solid var(--border-secondary);
          display: flex;
          flex-direction: column;
          overflow-y: auto;
        }

        .sidebar-header {
          padding: 24px 20px;
          border-bottom: 1px solid var(--border-secondary);
        }

        .app-title {
          font-size: 20px;
          font-weight: 700;
          color: var(--text-primary);
          margin: 0;
        }

        .app-subtitle {
          font-size: 14px;
          color: var(--accent-red);
          font-weight: 500;
        }

        .sidebar-nav {
          flex: 1;
          padding: 20px 12px;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .nav-section {
          margin-top: 24px;
          padding-top: 20px;
          border-top: 1px solid var(--border-secondary);
        }

        .nav-section-title {
          font-size: 12px;
          font-weight: 600;
          color: var(--text-tertiary);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 12px;
          padding: 0 16px;
        }

        .nav-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          border-radius: var(--radius-medium);
          color: var(--text-secondary);
          text-decoration: none;
          font-weight: 500;
          transition: all 0.2s ease;
          background: none;
          border: none;
          width: 100%;
          text-align: left;
          cursor: pointer;
        }

        .nav-item:hover {
          background: var(--bg-secondary);
          color: var(--text-primary);
        }

        .nav-item.active {
          background: var(--accent-red);
          color: white;
        }

        .nav-label {
          font-size: 14px;
        }

        .sidebar-footer {
          padding: 20px;
          border-top: 1px solid var(--border-secondary);
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .theme-toggle {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          background: var(--bg-secondary);
          border: none;
          border-radius: var(--radius-medium);
          color: var(--text-secondary);
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 14px;
        }

        .theme-toggle:hover {
          background: var(--border-secondary);
          color: var(--text-primary);
        }

        .user-info {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 16px;
          background: var(--bg-secondary);
          border-radius: var(--radius-medium);
        }

        .user-details {
          display: flex;
          flex-direction: column;
        }

        .user-name {
          font-size: 14px;
          font-weight: 600;
          color: var(--text-primary);
        }

        .user-role {
          font-size: 12px;
          color: var(--accent-red);
          font-weight: 500;
        }

        .logout-btn {
          background: none;
          border: none;
          color: var(--text-tertiary);
          cursor: pointer;
          padding: 4px;
          border-radius: 4px;
          transition: all 0.2s ease;
        }

        .logout-btn:hover {
          color: var(--accent-red);
          background: var(--bg-primary);
        }

        .main-content {
          flex: 1;
          overflow-y: auto;
          padding: 24px;
          background: var(--bg-secondary);
        }

        @media (max-width: 768px) {
          .sidebar {
            width: 100vw;
            position: fixed;
            z-index: 100;
            transform: translateX(-100%);
            transition: transform 0.3s ease;
          }

          .main-content {
            padding: 16px;
          }
        }
      `}</style>
    </div>
  );
};